<template>
  <div class="signature-list">
    <div v-if="!signatures.length" class="empty-state">
      <strong>Nenhuma assinatura cadastrada</strong>
      <p>
        Cadastre a primeira assinatura para começar a montar o módulo dinâmico.
      </p>
    </div>

    <article
      v-for="signature in signatures"
      :key="signature.id"
      class="signature-card"
    >
      <div class="signature-preview">
        <img
          v-if="signature.image?.dataUrl"
          :src="signature.image.dataUrl"
          :alt="`Assinatura de ${signature.signer?.name}`"
        />
      </div>

      <div class="signature-info">
        <strong>{{ signature.signer?.name }}</strong>

        <span v-if="signature.signer?.role">
          {{ signature.signer.role }}
        </span>

        <small v-if="signature.signer?.registry">
          {{ signature.signer.registry }}
        </small>

        <div class="signature-tags">
          <em>{{
            signature.placement?.page === "front" ? "Frente" : "Verso"
          }}</em>
          <em>
            {{ signature.placement?.topMm }}mm /
            {{ signature.placement?.leftMm }}mm
          </em>
        </div>
      </div>

      <div class="signature-actions">
        <button type="button" @click="$emit('edit', signature)">Editar</button>

        <button
          type="button"
          class="danger"
          @click="$emit('remove', signature)"
        >
          Excluir
        </button>
      </div>
    </article>
  </div>
</template>

<script setup>
defineProps({
  signatures: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["edit", "remove"]);
</script>

<style scoped>
.signature-list {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.empty-state {
  display: grid;
  place-items: center;
  min-height: 220px;
  border: 1px dashed rgba(148, 163, 184, 0.45);
  border-radius: 22px;
  text-align: center;
  background: linear-gradient(
      135deg,
      rgba(248, 250, 252, 0.95),
      rgba(241, 245, 249, 0.82)
    ),
    radial-gradient(
      circle at center,
      rgba(255, 122, 0, 0.08),
      transparent 18rem
    );
}

.empty-state strong {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1rem;
}

.empty-state p {
  max-width: 520px;
  margin: 0.45rem auto 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.6;
}

.signature-card {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem;
  border: 1px solid rgba(214, 222, 234, 0.95);
  border-radius: 22px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
  transition: 0.2s ease;
}

.signature-card:hover {
  transform: translateY(-1px);
  border-color: rgba(255, 122, 0, 0.34);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
}

.signature-preview {
  display: grid;
  place-items: center;
  min-height: 86px;
  border: 1px solid #e2e8f0;
  border-radius: 17px;
  background: linear-gradient(
      45deg,
      rgba(15, 23, 42, 0.04) 25%,
      transparent 25%
    ),
    linear-gradient(-45deg, rgba(15, 23, 42, 0.04) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(15, 23, 42, 0.04) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(15, 23, 42, 0.04) 75%);
  background-position: 0 0, 0 9px, 9px -9px, -9px 0;
  background-size: 18px 18px;
  background-color: #ffffff;
}

.signature-preview img {
  max-width: 150px;
  max-height: 68px;
  object-fit: contain;
}

.signature-info {
  min-width: 0;
  text-align: left;
}

.signature-info strong {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
}

.signature-info span {
  display: block;
  margin-top: 0.28rem;
  color: #334155;
  font-size: 0.8rem;
}

.signature-info small {
  display: block;
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.73rem;
}

.signature-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.72rem;
}

.signature-tags em {
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  color: #b45309;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  font-style: normal;
  background: #fff0dc;
}

.signature-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.signature-actions button {
  border: 0;
  border-radius: 12px;
  padding: 0.62rem 0.78rem;
  color: #334155;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.72rem;
  cursor: pointer;
  background: #f1f5f9;
  transition: 0.2s ease;
}

.signature-actions button:hover {
  color: #111827;
  background: #e2e8f0;
}

.signature-actions .danger {
  color: #b91c1c;
  background: #fee2e2;
}

.signature-actions .danger:hover {
  color: #7f1d1d;
  background: #fecaca;
}

@media (max-width: 860px) {
  .signature-card {
    grid-template-columns: 1fr;
  }

  .signature-actions {
    justify-content: flex-end;
  }
}
</style>
